import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";

import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import googleDriveAxios from "../../common/googleDriveAxios";

import { toast } from "react-toastify";
import CustomRadioGroup from "../CustomRadioGroup/CustomRadioGroup";
import CameraCapture from "../CameraCapture/CameraCapture";
import ImageList from "../ImageList/ImageList";

const FloorInspectionChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  canAutoSetChassisNo: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, canAutoSetChassisNo }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [checklist, setChecklist] = useState<any[]>([]);
  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);

  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");

  const hasUnsavedChanges = chassisNo;

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "floor-inspection",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [chassisNo, checkCheckpointRecord]);

  const fetchData = useCallback(async () => {
    if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
      setChecklist([]);
      setLocalCheckedBy("");
      setApiError(false);
      setModel("");
      setCheckedBy("");
      setCompleteStatus("Incomplete");
      return;
    }

    setLoading(true);
    setApiError(false);
    setModel("");
    setCheckedBy("");
    try {
      const response = await axiosPrivate.post(
        `/prepare_floor_inspection/${chassisNo}`
      );
      const data = response.data;

      const checklistData =
        data.floorQualityChecklistItems.map((item: any) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })) || [];
      console.log("Fetched checklist data:", checklistData);
      setChecklist(checklistData);
      setLocalCheckedBy(data.checkedBy || "");
      setCheckedBy(data.checkedBy || "");
      setModel(data.model || "Default Model");

      let formattedDate = "";
      if (data.createdOn) {
        const date = new Date(data.createdOn);
        if (date.getFullYear() !== 1900) {
          formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
        }
      }
      setDate(formattedDate);

      const isComplete =
        checklistData.length > 0 &&
        checklistData.every(
          (item: { status: string }) =>
            item.status === "pass" ||
            item.status === "fail" ||
            item.status === "n/a"
        );
      console.log(
        "Fetched isComplete:",
        isComplete,
        "Setting completeStatus:",
        isComplete ? "Complete" : "Incomplete"
      );
      setCompleteStatus(isComplete ? "Complete" : "Incomplete");
    } catch (error: any) {
      if (error.response?.status === 500) {
        setApiError(true);
      } else {
        toast.error("Failed to fetch data.");
      }
    } finally {
      setLoading(false);
    }
  }, [chassisNo, setModel, setCheckedBy, axiosPrivate]);

  useEffect(() => {
    fetchData();
  }, [chassisNo, setModel, setCheckedBy, axiosPrivate, fetchData]);

  const handleChecklistChange = (index: number, field: string, value: any) => {
    if (completeStatus === "Complete") return;
    setChecklist((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleRadioChange = (index: number, value: string) => {
    if (completeStatus === "Complete") return;
    const updatedChecklist = [...checklist];
    updatedChecklist[index].status = value.toLowerCase();
    setChecklist(updatedChecklist);
  };

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      const isComplete =
        checklist.length > 0 &&
        checklist.every(
          (item) =>
            item.status === "pass" ||
            item.status === "fail" ||
            item.status === "n/a"
        );
      console.log(
        "Saving - isComplete:",
        isComplete,
        "Current completeStatus:",
        completeStatus
      );

      const payload = {
        chassisNo,
        model: model,
        floorQualityChecklistItems: checklist.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
        completeStatus: isComplete ? "Complete" : "Incomplete",
      };

      await toast.promise(
        axiosPrivate.post("/create_or_update_floor_inspection_prep", payload),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      console.log(
        "Setting completeStatus after save:",
        isComplete ? "Complete" : "Incomplete"
      );
      setCompleteStatus(isComplete ? "Complete" : "Incomplete");

      await fetchData();

      if (checkedBy) {
        const response = await axiosPrivate.post(
          `prepare_rework/${chassisNo}`,
          null,
          {
            params: {
              rework_type: "floor-inspection",
              station_name: "floor-inspection",
            },
          }
        );

        let itemNoCounter = 1;
        const newReworkItems = checklist.flatMap((item) => {
          if (item.status === "fail") {
            const title = item.title || "";
            const notesStr = item.notes || "";
            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part: string) => part.trim())
                .filter((part: string) => part);
              return parts.map((note: string) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${notesStr}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          }
          return [];
        });

        const reworkPayload = {
          ...response.data,
          reworkItems: newReworkItems,
        };

        await axiosPrivate.post(`rework/save`, reworkPayload, {
          params: { rework_type: "floor-inspection" },
        });
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const nonSubheaderItems = checklist.filter((item) => !item.isSubheader);
  const allNotChecked =
    nonSubheaderItems.length > 0 &&
    nonSubheaderItems.every((item) => item.status === "not checked");

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box
                    sx={{
                      position: "absolute",
                      top: "60%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : apiError ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="error">Invalid DC Number</Typography>
                </TableCell>
              </TableRow>
            ) : (
              checklist.map((item, index) => {
                const serialNumber = checklist
                  .slice(0, index + 1)
                  .filter((i) => !i.isSubheader).length;

                return item.isSubheader ? (
                  <TableRow key={index}>
                    <TableCell
                      colSpan={4}
                      sx={{
                        backgroundColor: "#bdacd1",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      {item.title}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={index} sx={{ verticalAlign: "top" }}>
                    <TableCell
                      sx={{
                        width: "30px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        padding: "4px",
                        borderLeft: "1px solid #ccc",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <CameraCapture
                          chassisDCNumber={"DC" + chassisNo}
                          checkPointName={"checkpoint-" + serialNumber}
                          deptName="floor-inspection"
                          checkCheckpointRecord={checkCheckpointRecord}
                        />
                        <ImageList
                          chassisDCNumber={"DC" + chassisNo}
                          checkPointName={"checkpoint-" + serialNumber}
                          deptName="floor-inspection"
                          imagesExist={activeCheckpointList.includes(
                            "checkpoint-" + serialNumber
                          )}
                        />
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "40px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        padding: "4px",
                        borderLeft: "1px solid #ccc",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      <Typography>{serialNumber}</Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderLeft: "1px solid #d0d0d0",
                        wordWrap: "break-word",
                        whiteSpace: "pre-wrap",
                        verticalAlign: "top",
                        fontWeight:
                          !allNotChecked && item.status === "not checked"
                            ? "bold"
                            : "normal",
                      }}
                    >
                      {item.title && <Typography>{item.title}</Typography>}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderLeft: "1px solid #d0d0d0",
                        wordWrap: "break-word",
                        whiteSpace: "pre-wrap",
                        verticalAlign: "top",
                        fontWeight:
                          !allNotChecked && item.status === "not checked"
                            ? "bold"
                            : "normal",
                      }}
                    >
                      <TextField
                        placeholder="Type the note here..."
                        variant="standard"
                        value={item.notes}
                        onChange={(e) =>
                          handleChecklistChange(index, "notes", e.target.value)
                        }
                        size="small"
                        fullWidth
                        multiline
                        InputProps={{
                          readOnly: completeStatus === "Complete",
                          disableUnderline: true,
                          style: {
                            fontSize: "14px",
                            fontWeight:
                              !allNotChecked && item.status === "not checked"
                                ? "bold"
                                : "normal",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      className="p-0 px-2"
                      sx={{
                        borderLeft: "1px solid #d0d0d0",
                        borderRight: "1px solid #d0d0d0",
                        textAlign: "center",
                        verticalAlign: "center",
                        fontWeight:
                          !allNotChecked && item.status === "not checked"
                            ? "bold"
                            : "normal",
                      }}
                    >
                      <CustomRadioGroup
                        value={item.status}
                        onChange={(value) => handleRadioChange(index, value)}
                        options={[
                          { label: "Pass", value: "Pass" },
                          { label: "Not Checked", value: "Not Checked" },
                          { label: "Fail", value: "Fail" },
                          { label: "N/A", value: "N/A" },
                        ]}
                        disabled={completeStatus === "Complete"}
                        shouldBold={
                          !allNotChecked && item.status === "not checked"
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>
      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          checklist.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default FloorInspectionChecklist;
